import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPanel from './AdminPanel';
import AdminDashboard from './AdminDashboard';

const AdminArea = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('products'); // 'products' or 'images'
  const navigate = useNavigate();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Check authentication on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('adminAuth');
    const savedSessionAuth = sessionStorage.getItem('adminAuth');
    
    if (savedAuth) {
      // Decode credentials from localStorage (used by AdminPanel)
      try {
        const decoded = atob(savedAuth);
        const [username, password] = decoded.split(':');
        setCredentials({ username, password });
        setIsAuthenticated(true);
      } catch (e) {
        console.error('Failed to decode auth');
      }
    } else if (savedSessionAuth) {
      // Use credentials from sessionStorage (used by AdminDashboard)
      const auth = JSON.parse(savedSessionAuth);
      setCredentials(auth);
      setIsAuthenticated(true);
      // Also save to localStorage for consistency
      localStorage.setItem('adminAuth', btoa(`${auth.username}:${auth.password}`));
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${backendUrl}/api/admin/verify`, {
        method: 'GET',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        setIsAuthenticated(true);
        // Save to both storage methods for compatibility
        localStorage.setItem('adminAuth', btoa(`${credentials.username}:${credentials.password}`));
        sessionStorage.setItem('adminAuth', JSON.stringify(credentials));
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    localStorage.removeItem('adminAuth');
    sessionStorage.removeItem('adminAuth');
    navigate('/');
  };

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg shadow-2xl p-8 w-full max-w-md border border-gray-700">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Admin Login</h1>
            <p className="text-gray-400">Manage products and images</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2 text-sm font-medium">Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                placeholder="Enter username"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2 text-sm font-medium">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                placeholder="Enter password"
                required
              />
            </div>
            {error && (
              <div className="bg-red-500 bg-opacity-20 border border-red-500 text-red-300 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50 shadow-lg hover:shadow-xl"
            >
              {loading ? 'Logging in...' : 'Login to Admin Panel'}
            </button>
          </form>
          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 text-gray-400 hover:text-white transition text-sm"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Main Admin Area with Tab Switcher
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white">
      {/* Header with Navigation */}
      <div className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Title */}
            <h1 className="text-2xl font-bold text-white">Admin Control Panel</h1>
            
            {/* Tab Navigation */}
            <div className="flex items-center gap-2 bg-gray-900 p-1 rounded-lg">
              <button
                onClick={() => setActiveView('products')}
                className={`px-4 py-2 rounded-md font-semibold text-sm transition ${
                  activeView === 'products'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                📦 Product Manager
              </button>
              <button
                onClick={() => setActiveView('images')}
                className={`px-4 py-2 rounded-md font-semibold text-sm transition ${
                  activeView === 'images'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                🖼️ Image Manager
              </button>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition text-sm font-semibold shadow-md hover:shadow-lg"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="relative">
        {activeView === 'products' ? (
          <AdminPanel 
            isPreAuthenticated={true}
            onLogout={handleLogout}
          />
        ) : (
          <AdminDashboard 
            isPreAuthenticated={true}
            preAuthCredentials={credentials}
            onLogout={handleLogout}
          />
        )}
      </div>
    </div>
  );
};

export default AdminArea;
